import { useState, useEffect, useRef, useCallback } from 'react'
import { storiesApi, type Story } from '../../api/stories'
import { chatsApi } from '../../api/chats'
import { messagesApi } from '../../api/messages'
import { mediaApi } from '../../api/media'
import { useAuthStore } from '../../stores/authStore'
import { useChatStore } from '../../stores/chatStore'
import type { Chat } from '../../types'
import { chatTitle } from '../../lib/displayName'

interface StoryGroup {
  userId: string
  name: string
  avatarUrl?: string
  stories: Story[]
}

interface Highlight {
  id: string
  name: string
}

interface Props {
  groups: StoryGroup[]
  initialGroupIdx: number
  onClose: () => void
  highlights?: Highlight[]
}

const DURATION = 5000 // ms per story

export function StoryViewer({ groups, initialGroupIdx, onClose, highlights = [] }: Props) {
  const [groupIdx, setGroupIdx] = useState(initialGroupIdx)
  const [storyIdx, setStoryIdx] = useState(0)
  const [progress, setProgress] = useState(0)
  const [paused, setPaused] = useState(false)
  const [emoji, setEmoji] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [showViewers, setShowViewers] = useState(false)
  const [viewers, setViewers] = useState<Array<{ viewer_id: string; viewed_at: string }>>([])
  const [showHighlightPicker, setShowHighlightPicker] = useState(false)
  const [addingHighlight, setAddingHighlight] = useState<string | null>(null)
  const [showSharePicker, setShowSharePicker] = useState(false)
  const [shareSearch, setShareSearch] = useState('')
  const [sharingSent, setSharingSent] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { userId } = useAuthStore()
  const chats = useChatStore((s) => s.chats)

  const group = groups[groupIdx]
  const story = group?.stories[storyIdx]

  const advance = useCallback(() => {
    setProgress(0)
    if (storyIdx < group.stories.length - 1) {
      setStoryIdx((i) => i + 1)
    } else if (groupIdx < groups.length - 1) {
      setGroupIdx((g) => g + 1)
      setStoryIdx(0)
    } else {
      onClose()
    }
  }, [storyIdx, groupIdx, group?.stories.length, groups.length, onClose])

  const goBack = () => {
    setProgress(0)
    if (storyIdx > 0) {
      setStoryIdx((i) => i - 1)
    } else if (groupIdx > 0) {
      setGroupIdx((g) => g - 1)
      setStoryIdx(0)
    }
  }

  useEffect(() => {
    if (!story) return
    storiesApi.markViewed(story.id).catch(() => {})
  }, [story?.id])

  useEffect(() => {
    if (paused || !story) return
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          advance()
          return 0
        }
        return p + 100 / (DURATION / 100)
      })
    }, 100)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [paused, story?.id, advance])

  const handleReact = async (e: string) => {
    if (!story) return
    if (emoji === e) {
      await storiesApi.unreact(story.id).catch(() => {})
      setEmoji(null)
    } else {
      await storiesApi.react(story.id, e).catch(() => {})
      setEmoji(e)
    }
  }

  const sendReply = async () => {
    if (!replyText.trim() || !group || sendingReply) return
    setSendingReply(true)
    try {
      const chat = await chatsApi.getOrCreate(group.userId)
      await messagesApi.send(chat.id, {
        text: replyText.trim(),
        media_ids: story.media_file_id ? [story.media_file_id] : undefined,
      })
      setReplyText('')
    } catch { /* ignore */ }
    finally { setSendingReply(false) }
  }

  const handleDelete = async () => {
    if (!story) return
    await storiesApi.delete(story.id).catch(() => {})
    advance()
  }

  const handleAddToHighlight = async (highlightId: string) => {
    if (!story) return
    setAddingHighlight(highlightId)
    try {
      await storiesApi.addToHighlight(highlightId, story.id)
    } catch { /* ignore */ }
    finally { setAddingHighlight(null); setShowHighlightPicker(false) }
  }

  const shareStory = async (chat: Chat) => {
    if (!story || sharingSent) return
    setSharingSent(chat.id)
    try {
      const text = [
        '📖 История' + (group.name && group.userId !== userId ? ` от ${group.name}` : ''),
        story.text || '',
      ].filter(Boolean).join('\n')
      const media_ids = story.media_file_id ? [story.media_file_id] : undefined
      let targetChatId = chat.id
      if (chat.type === 'private' && chat.peer?.id) {
        const created = await chatsApi.getOrCreate(chat.peer.id)
        targetChatId = created.id
      }
      await messagesApi.send(targetChatId, { text, media_ids })
      setSharingSent(null)
      setShowSharePicker(false)
      setPaused(false)
    } catch {
      setSharingSent(null)
    }
  }

  const filteredChats = chats.filter((c) => {
    const title = chatTitle(c).toLowerCase()
    return !shareSearch || title.includes(shareSearch.toLowerCase())
  })

  const handleShowViewers = async () => {
    if (!story) return
    setShowViewers(true)
    setPaused(true)
    storiesApi.getViewers(story.id)
      .then((r) => setViewers(r.viewers ?? []))
      .catch(() => {})
  }

  if (!story) return null

  const isOwner = story.owner_id === userId
  const mediaUrl = story.media_file_id ? mediaApi.getURL(story.media_file_id) : null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Media / bg */}
        {mediaUrl ? (
          story.type === 'video' ? (
            <video
              src={mediaUrl}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              onMouseDown={() => setPaused(true)}
              onMouseUp={() => setPaused(false)}
            />
          ) : (
            <img
              src={mediaUrl}
              alt="story"
              className="absolute inset-0 w-full h-full object-cover"
              onMouseDown={() => setPaused(true)}
              onMouseUp={() => setPaused(false)}
            />
          )
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: story.bg_color || 'linear-gradient(135deg,#1a2a3a,#2d4a6a)' }}
          />
        )}

        {/* Text overlay */}
        {story.text && (
          <div className="absolute bottom-20 left-4 right-4 text-white text-center text-lg font-semibold drop-shadow-lg">
            {story.text}
          </div>
        )}

        {/* Top UI: progress bars + header */}
        <div className="absolute top-0 left-0 right-0 p-3 z-10">
          {/* Progress bars */}
          <div className="flex gap-1 mb-2">
            {group.stories.map((_, i) => (
              <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
                <div
                  className="h-full bg-white transition-none"
                  style={{
                    width: i < storyIdx ? '100%' : i === storyIdx ? `${progress}%` : '0%',
                  }}
                />
              </div>
            ))}
          </div>

          {/* User + time */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent/40 flex items-center justify-center shrink-0 text-sm font-bold text-white overflow-hidden">
              {group.avatarUrl
                ? <img src={group.avatarUrl} alt="" className="w-full h-full object-cover" />
                : group.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate drop-shadow">{group.name}</p>
              <p className="text-white/70 text-xs">
                {new Date(story.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            {isOwner && highlights.length > 0 && (
              <button onClick={() => { setShowHighlightPicker(true); setPaused(true) }}
                className="text-white/70 hover:text-white text-xs px-1.5" title="Добавить в подборку">
                📌
              </button>
            )}
            {isOwner && (
              <button onClick={handleDelete} className="text-white/70 hover:text-white text-xs px-2">Delete</button>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white text-lg px-1">✕</button>
          </div>
        </div>

        {/* Navigation tap zones */}
        <div className="absolute inset-y-0 left-0 w-1/3" onClick={goBack} />
        <div className="absolute inset-y-0 right-0 w-1/3" onClick={advance} />

        {/* Bottom bar: reply input + share + like */}
        <div
          className="absolute bottom-0 left-0 right-0 px-3 pb-4 pt-2 flex items-center gap-2"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {!isOwner ? (
            <>
              {/* Reply input */}
              <div className="flex-1 flex items-center bg-white/10 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 gap-2 focus-within:border-white/50 transition-colors">
                <input
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onFocus={() => setPaused(true)}
                  onBlur={() => { setPaused(false) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendReply() }}
                  placeholder="Ответить сообщением..."
                  className="flex-1 bg-transparent text-white placeholder-white/50 text-sm outline-none min-w-0"
                />
                {replyText.trim() && (
                  <button
                    onClick={sendReply}
                    disabled={sendingReply}
                    className="text-white/80 hover:text-white transition-colors shrink-0 disabled:opacity-40"
                  >
                    {sendingReply ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin"><circle cx="12" cy="12" r="10" strokeOpacity=".25"/><path d="M12 2a10 10 0 0 1 10 10" /></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
                    )}
                  </button>
                )}
              </div>
              {/* Share */}
              <button
                onClick={() => { setShowSharePicker(true); setPaused(true) }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white transition-colors shrink-0"
                title="Переслать"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 14 20 9 15 4"/>
                  <path d="M4 20v-7a4 4 0 0 1 4-4h12"/>
                </svg>
              </button>
              {/* Like / react */}
              <button
                onClick={() => handleReact('❤️')}
                className={`w-10 h-10 rounded-full backdrop-blur-sm border flex items-center justify-center transition-all shrink-0 ${emoji === '❤️' ? 'bg-red-500/40 border-red-400 scale-110' : 'bg-white/10 hover:bg-white/20 border-white/25'}`}
                title="Нравится"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill={emoji === '❤️' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </button>
            </>
          ) : (
            /* Owner: views count + share */
            <>
              <button
                onClick={handleShowViewers}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 text-white/80 hover:text-white text-sm transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                <span>{story.views_count} просмотров</span>
              </button>
              <div className="flex-1" />
              <button
                onClick={() => { setShowSharePicker(true); setPaused(true) }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white transition-colors shrink-0"
                title="Переслать"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 14 20 9 15 4"/>
                  <path d="M4 20v-7a4 4 0 0 1 4-4h12"/>
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Share to chat picker */}
        {showSharePicker && (
          <div
            className="absolute inset-0 z-20 bg-black/90 backdrop-blur-sm flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <p className="text-white font-semibold flex-1">Переслать в чат</p>
              <button
                onClick={() => { setShowSharePicker(false); setShareSearch(''); setPaused(false) }}
                className="text-white/70 hover:text-white text-lg"
              >✕</button>
            </div>
            <div className="px-4 py-2">
              <input
                autoFocus
                value={shareSearch}
                onChange={(e) => setShareSearch(e.target.value)}
                placeholder="Поиск чата..."
                className="w-full bg-white/10 text-white placeholder-white/40 rounded-xl px-3 py-2 text-sm outline-none border border-white/20 focus:border-white/40"
              />
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {filteredChats.length === 0 && (
                <p className="text-white/40 text-sm text-center py-6">Нет чатов</p>
              )}
              {filteredChats.map((chat) => {
                const title = chatTitle(chat)
                const isSending = sharingSent === chat.id
                return (
                  <button
                    key={chat.id}
                    onClick={() => shareStory(chat)}
                    disabled={!!sharingSent}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors disabled:opacity-60 text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-bold shrink-0 text-sm overflow-hidden">
                      {chat.peer?.avatar_url
                        ? <img src={mediaApi.getURL(chat.peer.avatar_url)} alt="" className="w-full h-full object-cover" />
                        : title[0]?.toUpperCase()}
                    </div>
                    <span className="text-white text-sm flex-1 truncate">{title}</span>
                    {isSending && (
                      <span className="text-white/50 text-xs shrink-0">Отправка…</span>
                    )}
                    {!isSending && sharingSent === null && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/40 shrink-0">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Add to Highlight picker */}
        {showHighlightPicker && (
          <div
            className="absolute inset-0 z-20 bg-black/80 backdrop-blur-sm flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <p className="text-white font-semibold flex-1">Add to Highlight</p>
              <button
                onClick={() => { setShowHighlightPicker(false); setPaused(false) }}
                className="text-white/70 hover:text-white text-lg"
              >✕</button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {highlights.map((h) => (
                <button
                  key={h.id}
                  disabled={addingHighlight === h.id}
                  onClick={() => handleAddToHighlight(h.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  <span className="text-xl">📌</span>
                  <span className="text-white text-sm">{h.name}</span>
                  {addingHighlight === h.id && (
                    <span className="ml-auto text-white/50 text-xs">Adding…</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Viewers list panel */}
        {showViewers && (
          <div
            className="absolute inset-0 z-20 bg-black/80 backdrop-blur-sm flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <p className="text-white font-semibold flex-1">Views ({viewers.length})</p>
              <button
                onClick={() => { setShowViewers(false); setPaused(false) }}
                className="text-white/70 hover:text-white text-lg"
              >✕</button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {viewers.length === 0 && (
                <p className="text-white/50 text-sm text-center py-8">No views yet</p>
              )}
              {viewers.map((v) => (
                <div key={v.viewer_id} className="flex items-center gap-3 px-4 py-2">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white text-xs shrink-0">
                    👤
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-mono truncate">…{v.viewer_id.slice(-8)}</p>
                    <p className="text-white/50 text-xs">
                      {new Date(v.viewed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
